import { Editor } from '@provision/rich-text-editor';
import { useCallback, useRef, useState } from 'react';

export interface UseAddLinkPopoverResult {
  anchorEl: HTMLElement | null;
  selectedText: string;
  selectionRef: React.MutableRefObject<Range | null>;
  openPopover: (text: string) => void;
  closePopover: () => void;
  handleLinkSubmit: (title: string, url: string) => void;
}

export interface UseAddLinkPopoverOptions {
  editor: Editor | null;
  onAfterSubmit?: () => void;
}

export const useAddLinkPopover = ({
  editor,
  onAfterSubmit,
}: UseAddLinkPopoverOptions): UseAddLinkPopoverResult => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const selectionRef = useRef<Range | null>(null);

  const cleanupRef = useRef<(() => void) | null>(null);

  const openPopover = useCallback(
    (text: string) => {
      setSelectedText(text);

      const selection = window.getSelection();

      if (editor?.view) {
        let rect: DOMRect;

        // check if there's a valid selection with content
        if (selection && selection.rangeCount > 0) {
          selectionRef.current = selection.getRangeAt(0);
          const range = selection.getRangeAt(0);
          rect = range.getBoundingClientRect();

          // if the selection has no dimensions (empty selection), use cursor position
          if (rect.width === 0 && rect.height === 0) {
            const { from } = editor.state.selection;
            const editorPos = editor.view.coordsAtPos(from);

            if (editorPos) {
              rect = new DOMRect(editorPos.left, editorPos.top, 0, 20); // 20px height for cursor
            }
          }
        } else {
          const { from } = editor.state.selection;
          const editorPos = editor.view.coordsAtPos(from);

          if (editorPos) {
            rect = new DOMRect(editorPos.left, editorPos.top, 0, 20);
          } else {
            // use editor's position
            rect = editor.view.dom.getBoundingClientRect();
          }
        }

        const tempElement = document.createElement('span');
        tempElement.style.position = 'absolute';
        tempElement.style.left = `${rect.left}px`;
        tempElement.style.top = `${rect.top}px`;
        document.body.appendChild(tempElement);

        setAnchorEl(tempElement);

        const cleanup = () => {
          document.body.removeChild(tempElement);
          setAnchorEl(null);
        };

        cleanupRef.current = cleanup;
      }
    },
    [editor],
  );

  const closePopover = useCallback(() => {
    if (selectionRef.current) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(selectionRef.current);
    }

    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  const handleLinkSubmit = useCallback(
    (title: string, url: string) => {
      if (!editor) return;

      const { empty } = editor.state.selection;

      if (!empty) {
        editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
      } else {
        editor
          .chain()
          .focus()
          .insertContent([
            {
              type: 'text',
              marks: [
                {
                  type: 'link',
                  attrs: {
                    href: url,
                    target: '_blank',
                  },
                },
              ],
              text: title,
            },
            {
              type: 'text',
              text: ' ', // Space after the link to let user continue typing
            },
          ])
          .run();
      }

      closePopover();
      onAfterSubmit?.();
    },
    [editor, closePopover, onAfterSubmit],
  );

  return {
    anchorEl,
    selectedText,
    selectionRef,
    openPopover,
    closePopover,
    handleLinkSubmit,
  };
};
